import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import {
  adminService,
  type RegistrationField,
  type RegistrationFieldWrite,
} from '../../services/adminService'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'

const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Single choice' },
  { value: 'checkbox', label: 'Checkbox' },
]

const HAS_OPTIONS = new Set(['select', 'radio'])

export default function RegistrationFieldsManager({
  eventId,
}: {
  eventId: string
}) {
  const { notify } = useToast()
  const [fields, setFields] = useState<RegistrationField[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<RegistrationField | null>(null)
  const [open, setOpen] = useState(false)

  const load = () =>
    adminService
      .listRegistrationFields(eventId)
      .then(setFields)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const remove = async (f: RegistrationField) => {
    if (!(await confirmDelete(`Delete field "${f.label}"?`))) return
    try {
      await adminService.deleteRegistrationField(eventId, f.id)
      notify('Field deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fields attendees complete when they register for this event.
      </Typography>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            setEditing(null)
            setOpen(true)
          }}
        >
          Add field
        </Button>
      </Stack>

      {fields.length === 0 ? (
        <EmptyState
          title="No registration fields"
          description="Add fields (e.g. dietary needs) to collect at registration."
        />
      ) : (
        <Stack spacing={1.5}>
          {fields.map((f) => (
            <Card
              key={f.id}
              variant="outlined"
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderRadius: 3,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {f.label}
                  {f.required ? ' *' : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {FIELD_TYPES.find((t) => t.value === f.fieldType)?.label ??
                    f.fieldType}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setEditing(f)
                  setOpen(true)
                }}
                size="small"
              >
                <EditRoundedIcon />
              </IconButton>
              <IconButton onClick={() => remove(f)} size="small" color="error">
                <DeleteRoundedIcon />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      {open && (
        <FieldDialog
          eventId={eventId}
          field={editing}
          count={fields.length}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            load()
          }}
        />
      )}
    </Box>
  )
}

function FieldDialog({
  eventId,
  field,
  count,
  onClose,
  onSaved,
}: {
  eventId: string
  field: RegistrationField | null
  count: number
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    label: field?.label ?? '',
    fieldType: field?.fieldType ?? 'text',
    required: field?.required ?? false,
    options: field?.options ?? '',
    helperText: field?.helperText ?? '',
  })
  const set = (k: keyof typeof form, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.label) {
      notify('Label is required', 'error')
      return
    }
    const payload: RegistrationFieldWrite = {
      label: form.label,
      fieldType: form.fieldType,
      required: form.required,
      options: HAS_OPTIONS.has(form.fieldType) ? form.options || null : null,
      helperText: form.helperText || null,
      ordinal: field?.ordinal ?? count,
    }
    setSaving(true)
    try {
      if (field)
        await adminService.updateRegistrationField(eventId, field.id, payload)
      else await adminService.createRegistrationField(eventId, payload)
      notify(field ? 'Field updated' : 'Field added')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{field ? 'Edit field' : 'Add field'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Label"
            required
            fullWidth
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
          />
          <TextField
            label="Type"
            select
            fullWidth
            value={form.fieldType}
            onChange={(e) => set('fieldType', e.target.value)}
          >
            {FIELD_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          {HAS_OPTIONS.has(form.fieldType) && (
            <TextField
              label="Options (one per line)"
              fullWidth
              multiline
              minRows={3}
              value={form.options}
              onChange={(e) => set('options', e.target.value)}
            />
          )}
          <TextField
            label="Helper text"
            fullWidth
            value={form.helperText}
            onChange={(e) => set('helperText', e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.required}
                onChange={(e) => set('required', e.target.checked)}
              />
            }
            label="Required"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
